import { Component, ElementRef } from '@angular/core';
import { StatusEnumDto } from 'app/@types/dto/enum-status-dto.model';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BatchUpdateRequestDto } from 'app/@types/dto/batch-update-request-dto.model';
import { TreeNode } from 'primeng/api';
import { AnalyticalCategoryDto } from 'app/@types/dto/analytical-category-dto.model';
import { OfficialCategoryDto } from 'app/@types/dto/official-category-dto.model';
import { revealFormErrors } from 'app/main/helpers/form-validation';

type BulkUpdateOption = 'statusId' | 'analyticalCategoryId' | 'ghgCategoryId' | 'dataUncertainty' | 'tagIds';

interface BulkUpdateModalData {
    option: BulkUpdateOption;
    selectedRowIds: string[];
    analyticalCategories: AnalyticalCategoryDto[];
    tagsTreeOptions: TreeNode[];
    ghgCategories: OfficialCategoryDto[];
}

interface BulkUpdateFormControls {
    status: FormControl<string | null>;
    analyticalCategory: FormControl<string | null>;
    ghgCategory: FormControl<string | null>;
    uncertainty: FormControl<string | null>;
    tagIds: FormControl<TreeNode[] | null>;
}

@Component({
    selector: 'app-bulk-update-modal',
    templateUrl: './bulk-update-modal.component.html',
    styleUrls: ['./bulk-update-modal.component.scss'],
    standalone: false,
})
export class BulkUpdateModalComponent {
    statusOptions = [
        {
            label: this.translateService.instant('advancedMeasure.status.ok'),
            value: StatusEnumDto.ok,
            key: 'ok',
            icon: 'check',
        },
        {
            label: this.translateService.instant('advancedMeasure.status.toCheck'),
            value: StatusEnumDto.toCheck,
            key: 'toCheck',
            icon: 'info',
        },
        {
            label: this.translateService.instant('advancedMeasure.status.error'),
            value: StatusEnumDto.error,
            key: 'error',
            icon: 'error',
        },
    ];

    uncertainty = [
        { label: this.translateService.instant('advancedMeasure.uncertainty.none'), value: 'none' },
        { label: this.translateService.instant('advancedMeasure.uncertainty.low'), value: 'low' },
        { label: this.translateService.instant('advancedMeasure.uncertainty.medium'), value: 'medium' },
        { label: this.translateService.instant('advancedMeasure.uncertainty.high'), value: 'high' },
    ];

    form: FormGroup<BulkUpdateFormControls>;
    private readonly data: BulkUpdateModalData;

    constructor(
        // Keep `dialogConfig` untyped so the template (which reads dialogConfig.data.* directly)
        // continues to type-check; the component-side reads go through the typed `this.data`.
        public dialogConfig: DynamicDialogConfig,
        public ref: DynamicDialogRef,
        private translateService: CustomTranslateService,
        private fb: FormBuilder,
        private host: ElementRef<HTMLElement>,
    ) {
        this.data = (dialogConfig.data as BulkUpdateModalData | undefined) ?? {
            option: 'statusId',
            selectedRowIds: [],
            analyticalCategories: [],
            tagsTreeOptions: [],
            ghgCategories: [],
        };
        const data = this.data;
        this.form = new FormGroup<BulkUpdateFormControls>({
            status: new FormControl<string | null>(null, data.option === 'statusId' ? Validators.required : null),
            analyticalCategory: new FormControl<string | null>(
                null,
                data.option === 'analyticalCategoryId' ? Validators.required : null,
            ),
            ghgCategory: new FormControl<string | null>(
                null,
                data.option === 'ghgCategoryId' ? Validators.required : null,
            ),
            uncertainty: new FormControl<string | null>(
                null,
                data.option === 'dataUncertainty' ? Validators.required : null,
            ),
            tagIds: new FormControl<TreeNode[] | null>(null, data.option === 'tagIds' ? Validators.required : null),
        });
    }

    onCancel() {
        this.ref.close();
    }

    onSubmit() {
        if (!revealFormErrors(this.form, this.host.nativeElement)) {
            return;
        }
        const controls = this.form.controls;
        const tagIdsValue = controls.tagIds.value;
        const request: BatchUpdateRequestDto = {
            questionnaireRowIds: this.data.selectedRowIds,
            statusId: controls.status.value ?? undefined,
            analyticalCategoryId: controls.analyticalCategory.value ?? undefined,
            ghgCategoryId: controls.ghgCategory.value ?? undefined,
            dataUncertainty: controls.uncertainty.value ?? undefined,
            tagIds: tagIdsValue
                ? tagIdsValue.map((tag) => tag.key).filter((key): key is string => key !== undefined)
                : undefined,
        };
        this.ref.close(request);
    }

    /**
     * Returns the CSS class corresponding to the status for styling
     * @param status - The status key ('ok', 'toCheck', 'error')
     * @returns The CSS class associated with the status
     */
    getStatusClass(status: string): string {
        switch (status) {
            case 'ok':
                return 'status-ok';
            case 'toCheck':
                return 'status-toCheck';
            case 'error':
                return 'status-error';
            default:
                return '';
        }
    }
}
